import { memo, useMemo } from 'react';

import { Stack, Tooltip, Typography } from '@mui/material';
import { uniqueBy } from '@realadvisor/array';

import type { LeadsListItemFragment } from '@graphql/__generated__/graphql';
import IndicatorLeadStage from '@modules/_shared/components/IndicatorLeadStage';
import { useLeadsFilters } from '@modules/leads/components/context/leadsFiltersContext';
import { sortBySourceThenCreatedAt } from '@modules/leads/utils/sort';
import { useLocale } from '@shared/hooks/locale';

type LeadAgents = LeadsListItemFragment['lead_agents'];
type LeadStageCellProps = {
  leadAgents: LeadAgents;
  requalifiedAt: string | null;
};

export const LeadStageCell = memo(
  ({ leadAgents, requalifiedAt }: LeadStageCellProps) => {
    const { t } = useLocale();
    const leadFilters = useLeadsFilters();

    const firstStage = useMemo(() => {
      // We want to display first the agents that are filtered and then the others sorted by source and created at
      return leadAgents
        .filter(la => la.lead_stage != null)
        .sort((la1, la2) => {
          const la1StageFilterWeight = leadFilters.stageIds.includes(
            la1.stage_id ?? '',
          )
            ? 1
            : 0;
          const la2StageFilterWeight = leadFilters.stageIds.includes(
            la2.stage_id ?? '',
          )
            ? 1
            : 0;

          const la1AgentFilterWeight = leadFilters.agentIds.includes(
            la1.user_id,
          )
            ? 1
            : 0;
          const la2AgentFilterWeight = leadFilters.agentIds.includes(
            la2.user_id,
          )
            ? 1
            : 0;

          return (
            la2StageFilterWeight - la1StageFilterWeight ||
            la2AgentFilterWeight - la1AgentFilterWeight ||
            sortBySourceThenCreatedAt(la1, la2)
          );
        })[0]?.lead_stage;
    }, [leadAgents, leadFilters]);

    const uniqueStages = useMemo(
      () =>
        uniqueBy(
          leadAgents.filter(la => la.stage_id != null),
          la => la.stage_id,
        ),
      [leadAgents],
    );

    if (firstStage == null) {
      return (
        <div>
          <IndicatorLeadStage status="active">
            <div>{requalifiedAt != null ? t('Requalified') : t('New')}</div>
          </IndicatorLeadStage>
        </div>
      );
    }

    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <IndicatorLeadStage status={firstStage.status}>
          <div>{firstStage.label ?? '-'}</div>
        </IndicatorLeadStage>
        {uniqueStages.length > 1 && (
          <Tooltip
            title={uniqueStages
              .map(la => la.lead_stage?.label)
              .filter(Boolean)
              .join(', ')}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              (+{uniqueStages.length - 1})
            </Typography>
          </Tooltip>
        )}
      </Stack>
    );
  },
);
